<template>
  <div>
    <CommonSection />
    <div class="container-fluid">
      <v-row>
        <v-col>
          <v-alert
            v-if="emailWarning.hasEmailSentToday"
            dismissible
            class="mb-4 email-warning-alert"
            @input="emailWarning.hasEmailSentToday = false"
          >
            Notification emails have already been sent today, with the last one sent at {{ formatLastSentTime(emailWarning.date) }}.
          </v-alert>
          <card shadow type="secondary">
            <div slot="header" class="bg-white border-0">
              <h3 class="mb-0 float-left card-header-name">
                Activity Logs
              </h3>
              <v-btn
                v-if="selectedNotifications.length > 0"
                style="background-color: white; color: #344452;"
                class="float-right"
                @click="sendNotificationDialog = true"
              >
                Send Notification ({{ selectedNotifications.length }})
              </v-btn>
              <v-dialog v-model="sendNotificationDialog" persistent max-width="300">
                <v-card>
                  <v-card-title class="card-title">Send Notification</v-card-title>
                  <v-card-text>
                    Are you sure you want to send
                    <strong>{{ selectedNotifications.length }}</strong> notification(s)?
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red darken-1" text @click="sendNotificationDialog = false">Cancel</v-btn>
                    <v-btn color="green darken-1" text @click="confirmSendNotifications">Send</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </div>
            <v-card>
              <v-card-title>
                <v-row>
                  <v-col>
                    <v-select
                      :items="notificationTypeArr"
                      item-value="value"
                      item-text="name"
                      label="Select Log Type"
                      v-model="notificationType"
                      @change="typeFilter"
                    ></v-select>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-data-table
                v-model="selectedNotifications"
                :headers="headers"
                :items="notifications"
                :loading="loading"
                loading-text="Loading... Please wait"
                :server-items-length="notifications.length"
                :show-select="true"
                item-key="_id"
                hide-default-footer
                class="notification-table"
              >
                <template v-slot:[`item.notificationType`]="{ item }">
                  <div>{{ formatNotificationType(item.notificationType) }}</div>
                </template>

                <template v-slot:[`item.createdAt`]="{ item }">
                  <div v-if="item.createdAt">
                    <span v-if="item.createdAt.$date">{{ item.createdAt.$date | moment("LL") }}</span>
                    <span v-else-if="typeof item.createdAt === 'string'">{{ item.createdAt | moment("LL") }}</span>
                    <span v-else-if="item.createdAt instanceof Date">{{ item.createdAt | moment("LL") }}</span>
                    <span v-else>{{ item.createdAt }}</span>
                  </div>
                  <div v-else>--</div>
                </template>

                <template v-slot:[`item.metadata`]="{ item }">
                  <div class="metadata-content details-cell">
                    <div v-if="item.notificationType === 'PRODUCT_PUBLISHED'">
                      <div><strong>Product:</strong> {{ item.metadata.productName }}</div>
                      <div v-if="item.metadata.brandName"><strong>Brand:</strong> {{ item.metadata.brandName }}</div>
                      <div v-if="item.metadata.strain"><strong>Strain:</strong> {{ item.metadata.strain }}</div>
                      <div v-if="item.metadata.availability"><strong>Availability:</strong> {{ item.metadata.availability }}</div>
                      <div v-if="item.metadata.price && item.metadata.price.length > 0">
                        <strong>Price:</strong> 
                        <span v-for="(price, index) in item.metadata.price" :key="index">
                          {{ price.price }}{{ item.metadata.price.length > 1 && index < item.metadata.price.length - 1 ? ', ' : '' }}
                        </span>
                      </div>
                    </div>
                    <div v-else-if="item.notificationType === 'PRICE_CHANGED'">
                      <div><strong>Product:</strong> {{ item.metadata.productName }}</div>
                      <div v-if="item.metadata.brandName"><strong>Brand:</strong> {{ item.metadata.brandName }}</div>
                      <div v-if="item.metadata.strain"><strong>Strain:</strong> {{ item.metadata.strain }}</div>
                      <div v-if="item.metadata.previousPrice && item.metadata.previousPrice.length > 0">
                        <strong>Previous Price:</strong> 
                        <span v-for="(price, index) in item.metadata.previousPrice" :key="index">
                          {{ price.price }}{{ item.metadata.previousPrice.length > 1 && index < item.metadata.previousPrice.length - 1 ? ', ' : '' }}
                        </span>
                      </div>
                      <div v-if="item.metadata.price && item.metadata.price.length > 0">
                        <strong>New Price:</strong> 
                        <span v-for="(price, index) in item.metadata.price" :key="index">
                          {{ price.price }}{{ item.metadata.price.length > 1 && index < item.metadata.price.length - 1 ? ', ' : '' }}
                        </span>
                      </div>
                    </div>
                    <div v-else-if="item.notificationType === 'NEWSFEED_PUBLISHED'">
                      <div><strong>Title:</strong> {{ item.metadata.title }}</div>
                      <div v-if="item.metadata.categoryName"><strong>Category:</strong> {{ item.metadata.categoryName }}</div>
                      <div v-if="item.metadata.summary"><strong>Summary:</strong> {{ item.metadata.summary.substring(0, 100) }}{{ item.metadata.summary.length > 100 ? '...' : '' }}</div>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <v-dialog v-model="deleteDialog[item._id]" persistent max-width="300">
                    <template v-slot:activator="{ on }">
                      <v-tooltip
                        bottom
                        :color="$vuetify.theme.themes.light.tooltipColor"
                      >
                        <template v-slot:activator="{ on: tooltip, attrs }">
                          <img
                            v-bind="attrs"
                            v-on="{ ...on, ...tooltip }"
                            class="mr-2 remove-icon"
                            src="@/assets/images/Remove.svg"
                            style="cursor: pointer;"
                          />
                        </template>
                        <span>Delete</span>
                      </v-tooltip>
                    </template>
                    <v-card>
                      <v-card-title class="card-title">Delete Notification</v-card-title>
                      <v-card-text>
                        Are you sure you want to
                        <strong>DELETE</strong> this notification?
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="red darken-1" text @click="cancelDelete(item._id)">Cancel</v-btn>
                        <v-btn color="green darken-1" text @click="confirmDelete(item._id)">Delete</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </template>
              </v-data-table>
            </v-card>
          </card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import CommonSection from "@/components/CommonHeader";
import { NotificationService } from "@/services";

export default {
  name: "Notifications",
  components: {
    CommonSection,
  },
  data: () => ({
    loading: true,
    notifications: [],
    selectedNotifications: [],
    notificationType: "all",
    sendNotificationDialog: false,
    emailWarning: {
      hasEmailSentToday: false,
      count: 0,
      date: null,
    },
    notificationTypeArr: [
      {
        name: "All",
        value: "all",
      },
      {
        name: "Product Published",
        value: "PRODUCT_PUBLISHED",
      },
      {
        name: "Price Changed",
        value: "PRICE_CHANGED",
      },
      {
        name: "Newsfeed Published",
        value: "NEWSFEED_PUBLISHED",
      },
    ],
    headers: [
      {
        text: "Log Type",
        align: "start",
        value: "notificationType",
      },
      {
        text: "Details",
        value: "metadata",
        sortable: false,
      },
      {
        text: "Created At",
        value: "createdAt",
        width: 200,
      },
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        width: 100,
      },
    ],
    deleteDialog: {},
  }),
  created() {
    this.initialize();
    this.checkEmailSentToday();
  },
  methods: {
    formatNotificationType(type) {
      const typeMap = {
        PRODUCT_PUBLISHED: "Product Published",
        PRICE_CHANGED: "Price Changed",
        NEWSFEED_PUBLISHED: "Newsfeed Published",
      };
      return typeMap[type] || type;
    },
    async initialize() {
      this.loading = true;
      try {
        const params = {};
        
        if (this.notificationType && this.notificationType !== "all") {
          params.notificationType = this.notificationType;
        }

        const response = await NotificationService.getNotifications(params);
        console.log('Notification API Response:', response);
        
        // Handle different possible response structures
        let notificationsData = [];
        
        if (response && response.data) {
          // Check if data is directly in response.data (array)
          if (Array.isArray(response.data)) {
            notificationsData = response.data;
          }
          // Check if data is in response.data.data (standard format)
          else if (response.data.data && Array.isArray(response.data.data)) {
            notificationsData = response.data.data;
          }
          // Check if data is in response.data.result or response.data.notifications
          else if (response.data.result && Array.isArray(response.data.result)) {
            notificationsData = response.data.result;
          }
          else if (response.data.notifications && Array.isArray(response.data.notifications)) {
            notificationsData = response.data.notifications;
          }
        }

        this.notifications = notificationsData;
        this.loading = false;
      } catch (error) {
        console.error('Error fetching notifications:', error);
        this.loading = false;
        this.notifications = [];
      }
    },
    typeFilter() {
      this.initialize();
    },
    async checkEmailSentToday() {
      try {
        const response = await NotificationService.checkTodayEmailSent();
        if (response && response.data) {
          this.emailWarning = {
            hasEmailSentToday: response.data.hasEmailSentToday || false,
            count: response.data.count || 0,
            date: response.data.date || null,
          };
        }
      } catch (error) {
        console.error('Error checking today email sent:', error);
      }
    },
    formatLastSentTime(date) {
      if (!date) return 'N/A';
      
      // Handle different date formats
      let dateObj;
      if (date.$date) {
        dateObj = new Date(date.$date);
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
      } else if (date instanceof Date) {
        dateObj = date;
      } else {
        return 'N/A';
      }
      
      // Format as "8:45 AM GMT"
      const hours = dateObj.getUTCHours();
      const minutes = dateObj.getUTCMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      
      return `${displayHours}:${displayMinutes} ${ampm} GMT`;
    },
    async confirmSendNotifications() {
      this.sendNotificationDialog = false;
      if (this.selectedNotifications.length > 0) {
        try {
          const notificationIds = this.selectedNotifications.map(notif => notif._id);
          const response = await NotificationService.markAsEmailSent(notificationIds);
          if (response) {
            this.$store.dispatch("user/setSnack", `${this.selectedNotifications.length} notification(s) sent successfully!`);
            this.$store.dispatch("user/setType", "info");
            this.selectedNotifications = [];
            this.initialize();
            this.checkEmailSentToday(); // Refresh warning after sending
          }
        } catch (error) {
          console.error('Error sending notifications:', error);
          this.$store.dispatch("user/setSnack", "Failed to send notifications");
          this.$store.dispatch("user/setType", "error");
        }
      }
    },
    cancelDelete(id) {
      this.$set(this.deleteDialog, id, false);
    },
    async confirmDelete(id) {
      this.$set(this.deleteDialog, id, false);
      try {
        const response = await NotificationService.markAsDeleted([id]);
        if (response) {
          this.$store.dispatch("user/setSnack", "Notification deleted successfully!");
          this.$store.dispatch("user/setType", "info");
          this.initialize();
        }
      } catch (error) {
        console.error('Error deleting notification:', error);
        this.$store.dispatch("user/setSnack", "Failed to delete notification");
        this.$store.dispatch("user/setType", "error");
      }
    },
  },
};
</script>

<style scoped>
.metadata-content {
  max-width: 500px;
  word-wrap: break-word;
  margin: 0 !important;
  padding: 8px 0;
}

.metadata-content div {
  margin-bottom: 0;
  padding: 0;
  line-height: 1.4;
}

.metadata-content div:not(:last-child) {
  margin-bottom: 4px;
}

::v-deep .notification-table .v-data-table__wrapper tbody tr {
  background-color: white !important;
}

::v-deep .notification-table .v-data-table__wrapper tbody tr:nth-child(even) {
  background-color: white !important;
}

::v-deep .notification-table .v-data-table__wrapper tbody tr:hover {
  background-color: #f5f5f5 !important;
}

::v-deep .notification-table .v-data-table tbody td {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  vertical-align: top;
}

/* Ensure details column has proper padding */
::v-deep .notification-table .v-data-table tbody td:nth-child(4) {
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}

.send-notification-btn {
  color: #344452;
  font-weight: 600;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  cursor: pointer;
  display: inline-block;
}

.send-notification-btn:hover {
  background-color: #f5f5f5;
  color: #344452;
}

.email-warning-alert {
  background-color: #FBBF24 !important;
  border-color: #FBBF24 !important;
}

.email-warning-alert .v-alert__content {
  color: #1F2937 !important;
  font-weight: 500;
}

.email-warning-alert .v-alert__dismissible {
  color: #1F2937 !important;
}
</style>
